// Piezas compartidas de la Hoja de ruta: fechas, colores, calendario por meses y línea de tiempo.
// Las usan la hoja editable (/hoja-de-ruta/:id) y la vista pública que se comparte por enlace.
import { useState } from "react";
import { CheckCircle2, Circle, CircleDot, Ban, AlertTriangle, Lock } from "lucide-react";

export const PHASES = [
  ["voces", "Voces"], ["beat", "Beat"], ["mezcla", "Mezcla"], ["master", "Máster"],
  ["portada", "Portada"], ["video", "Video"], ["legal", "Legal"], ["distribucion", "Distribución"],
];
export const STATUS = {
  pendiente: { label: "Pendiente", cls: "border-slate-300 bg-white text-slate-500", Icon: Circle },
  en_curso: { label: "En curso", cls: "border-sky-300 bg-sky-50 text-sky-700", Icon: CircleDot },
  hecho: { label: "Hecho", cls: "border-emerald-300 bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  no_aplica: { label: "No aplica", cls: "border-slate-200 bg-slate-50 text-slate-400", Icon: Ban },
  bloqueado: { label: "Bloqueado", cls: "border-red-300 bg-red-50 text-red-700", Icon: AlertTriangle },
};
export const KINDS = { single: "Single", ep: "EP", album: "Álbum", campana: "Campaña", gira: "Gira", otro: "Otro" };
export const EVENT = {
  salida: { label: "Salida", cls: "border-emerald-400 text-emerald-700 bg-emerald-50", day: "bg-emerald-500 text-white" },
  entrega: { label: "Entrega", cls: "border-sky-400 text-sky-700 bg-sky-50", day: "bg-sky-200 text-sky-900" },
  contenido: { label: "Contenido", cls: "border-pink-400 text-pink-700 bg-pink-50", day: "bg-pink-200 text-pink-900" },
  hito: { label: "Hito", cls: "border-slate-400 text-slate-700 bg-slate-50", day: "bg-slate-300 text-slate-900" },
  pago: { label: "Pago", cls: "border-amber-400 text-amber-700 bg-amber-50", day: "bg-amber-200 text-amber-900" },
};
export const MILESTONE_TYPES = { hito: "Hito", reunion: "Reunión", entrevista: "Entrevista", concierto: "Concierto", sesion: "Sesión de estudio", rodaje: "Rodaje", pago: "Pago", prensa: "Prensa" };
export const PLATFORMS = [["instagram", "Instagram"], ["tiktok", "TikTok"], ["youtube", "YouTube"], ["offline", "Offline / prensa"]];

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const isDate = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
// Fechas "AAAA-MM-DD" sin zona horaria: nunca se corren un día ni salen "undefined".
export const fmtDate = (s, withYear = false) => {
  if (!isDate(s)) return "";
  const [y, m, d] = s.split("-").map(Number);
  return `${d} de ${MONTHS[m - 1]}${withYear ? ` de ${y}` : ""}`;
};
export const fmtShort = (s) => (isDate(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : "");
export const monthTitle = (ym) => { const [y, m] = ym.split("-").map(Number); return `${MONTHS[m - 1]} ${y}`; };
export const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
export const daysUntil = (s) => {
  if (!isDate(s)) return null;
  const [y, m, d] = s.split("-").map(Number);
  const t = new Date(); const a = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate()); const b = Date.UTC(y, m - 1, d);
  return Math.round((b - a) / 86400000);
};
export const addMonths = (ym, n) => { let [y, m] = ym.split("-").map(Number); m += n; while (m > 12) { m -= 12; y += 1; } return `${y}-${String(m).padStart(2, "0")}`; };

export const money = (v, cur = "USD") => {
  const n = Number(v || 0);
  try { return n.toLocaleString("es-VE", { style: "currency", currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  catch (e) { return `${n.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`; }
};
export const moneyMap = (obj) => {
  const parts = Object.entries(obj || {}).filter(([, v]) => Number(v)).map(([c, v]) => money(v, c));
  return parts.length ? parts.join(" + ") : "—";
};

/** Todos los eventos con fecha: salidas, entregas, contenido por plataforma e hitos sueltos. */
export function buildEvents(doc, effective = {}) {
  const ev = [];
  const songs = doc?.songs || [];
  const name = (id) => songs.find((s) => s.id === id)?.title || "";
  for (const s of songs) {
    if (isDate(s.release_date)) ev.push({ date: s.release_date, type: "salida", text: `Sale «${s.title}»`, song: s.title, song_id: s.id });
    const byDate = {}; // varias fases el mismo día = una sola línea ("Voces · Beat · Mezcla")
    for (const [k, label] of PHASES) {
      const d = s.phases?.[k]?.date;
      if (!isDate(d)) continue;
      const st = (effective[s.id] || {})[k]?.status || s.phases?.[k]?.status;
      (byDate[d] = byDate[d] || []).push(`${label}${st === "hecho" ? " ✓" : ""}`);
    }
    for (const [d, labels] of Object.entries(byDate)) ev.push({ date: d, type: "entrega", text: labels.join(" · "), song: s.title, song_id: s.id });
    if (isDate(s.video?.due)) ev.push({ date: s.video.due, type: "entrega", text: "Video", song: s.title, song_id: s.id });
    if (isDate(s.cover?.due)) ev.push({ date: s.cover.due, type: "entrega", text: "Portada", song: s.title, song_id: s.id });
    for (const m of s.marketing || []) {
      if (!isDate(m.date)) continue;
      const parts = PLATFORMS.filter(([k]) => m[k]).map(([k, l]) => ({ k, l, t: m[k] }));
      if (parts.length) ev.push({ date: m.date, type: "contenido", parts, song: s.title, song_id: s.id });
    }
    for (const b of s.budget || []) {
      if (!b.paid && isDate(b.due)) ev.push({ date: b.due, type: "pago", text: `Pago: ${b.concept || "gasto"}`, song: s.title, song_id: s.id });
    }
  }
  for (const m of doc?.milestones || []) {
    if (isDate(m.date)) ev.push({ date: m.date, type: m.type === "pago" ? "pago" : "hito", text: m.text, done: m.done, song: name(m.song_id), milestone_id: m.id, mtype: m.type });
  }
  const order = { salida: 0, entrega: 1, contenido: 2, pago: 3, hito: 4 };
  ev.sort((a, b) => (a.date === b.date ? order[a.type] - order[b.type] : a.date < b.date ? -1 : 1));
  return ev;
}

export function MonthsCalendar({ startMonth, months, events, onPickDay }) {
  const byDay = {};
  for (const e of events) (byDay[e.date] = byDay[e.date] || []).push(e);
  const first = /^\d{4}-\d{2}$/.test(startMonth || "") ? startMonth : todayIso().slice(0, 7);
  const list = Array.from({ length: Math.max(1, Math.min(Number(months) || 6, 24)) }, (_, i) => addMonths(first, i));
  const today = todayIso();
  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-4" data-testid="rm-calendar">
      {list.map((ym) => {
        const [y, m] = ym.split("-").map(Number);
        const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7; // lunes primero
        const days = new Date(y, m, 0).getDate();
        return (
          <div key={ym} className="rounded-xl border border-slate-200 bg-white p-2 sm:p-3 break-inside-avoid">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-black">{monthTitle(ym)}</div>
            <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-slate-400">
              {["L", "M", "X", "J", "V", "S", "D"].map((d) => <div key={d}>{d}</div>)}
              {Array.from({ length: lead }, (_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: days }, (_, i) => {
                const iso = `${ym}-${String(i + 1).padStart(2, "0")}`;
                const evs = byDay[iso] || [];
                const top = evs[0];
                const title = evs.map((e) => `${EVENT[e.type].label}: ${e.text || e.parts?.map((p) => `${p.l} ${p.t}`).join(" · ")}${e.song ? ` (${e.song})` : ""}`).join("\n");
                return (
                  <button
                    type="button" key={iso} title={title || undefined} disabled={!onPickDay && !evs.length}
                    onClick={() => onPickDay && onPickDay(iso)}
                    className={`relative aspect-square rounded-md text-[11px] leading-none transition ${top ? `${EVENT[top.type].day} font-bold` : "text-slate-600 hover:bg-slate-100"} ${iso === today ? "ring-2 ring-black" : ""}`}
                  >
                    {i + 1}
                    {evs.length > 1 && <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-black/60" />}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Legend() {
  return (
    <div className="flex flex-wrap gap-2 text-[11px]">
      {Object.entries(EVENT).map(([k, v]) => (
        <span key={k} className={`rounded-full border px-2 py-0.5 font-semibold ${v.cls}`}>{v.label}</span>
      ))}
    </div>
  );
}

export function Timeline({ events: all, onRemoveMilestone, onToggleMilestone, readOnly, hideOld = true }) {
  const [showOld, setShowOld] = useState(!hideOld);
  const cut = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const old = all.filter((e) => e.date < cut).length;
  const events = showOld ? all : all.filter((e) => e.date >= cut);
  const toggle = old > 0 && (
    <button type="button" onClick={() => setShowOld(!showOld)} className="mb-2 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-black print:hidden">
      {showOld ? "Ocultar lo que ya pasó" : `Ver lo que ya pasó (${old})`}
    </button>
  );
  if (!all.length) return <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">Todavía no hay nada fechado. Pon la fecha de salida de cada canción o añade un hito.</p>;
  const groups = [];
  for (const e of events) {
    const ym = e.date.slice(0, 7);
    if (!groups.length || groups[groups.length - 1].ym !== ym) groups.push({ ym, items: [] });
    groups[groups.length - 1].items.push(e);
  }
  const today = todayIso();
  return (
    <div>
    {toggle}
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="rm-timeline">
      {groups.map((g) => (
        <div key={g.ym} className="break-inside-avoid">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-600">{monthTitle(g.ym)}</div>
          {g.items.map((e, i) => (
            <div key={`${e.date}-${i}`} className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-100 px-4 py-2.5 text-sm last:border-0 ${e.date < today ? "opacity-60" : ""}`}>
              <span className="w-28 shrink-0 text-xs text-slate-500">{fmtDate(e.date)}</span>
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${EVENT[e.type].cls}`}>{e.mtype && e.mtype !== "hito" && e.mtype !== "pago" ? MILESTONE_TYPES[e.mtype] : EVENT[e.type].label}</span>
              <span className={`min-w-0 flex-1 text-black ${e.done ? "line-through text-slate-400" : ""}`}>
                {e.parts ? e.parts.map((p) => (
                  <span key={p.k} className="mr-3 inline-flex items-center gap-1"><span className="rounded border border-pink-300 px-1 text-[9px] font-bold uppercase text-pink-700">{p.l}</span>{p.t}</span>
                )) : e.text}
                {e.song && <span className="ml-2 text-xs text-slate-400">· {e.song}</span>}
              </span>
              {e.milestone_id && !readOnly && (
                <span className="flex gap-1 print:hidden">
                  <button type="button" onClick={() => onToggleMilestone(e.milestone_id)} className="rounded border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50">{e.done ? "Reabrir" : "Hecho"}</button>
                  <button type="button" onClick={() => onRemoveMilestone(e.milestone_id)} className="rounded border border-red-200 px-2 py-0.5 text-[11px] text-red-600 hover:bg-red-50">Quitar</button>
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
    </div>
  );
}

export function StatusPill({ status, auto, note }) {
  const s = STATUS[status] || STATUS.pendiente;
  return (
    <span title={auto ? `Se marca solo: ${note || ""}` : undefined} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-bold uppercase tracking-wide ${s.cls}`}>
      {auto ? <Lock className="h-3 w-3" /> : <s.Icon className="h-3 w-3" />}{s.label}
    </span>
  );
}

export function ProgressBar({ value, className = "" }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={`h-1.5 w-full overflow-hidden rounded-full bg-slate-200 ${className}`}>
      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${v}%` }} />
    </div>
  );
}

export function Section({ n, title, desc, right, children, id }) {
  return (
    <section id={id} className="scroll-mt-24 space-y-4 break-inside-avoid-page">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 pb-3">
        <div>
          {n && <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#CA0808]">{n}</div>}
          <h2 className="font-serif text-2xl font-bold tracking-tight text-black">{title}</h2>
          {desc && <p className="mt-1 max-w-2xl text-xs text-slate-500">{desc}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------- vistas propias de Always

const dayNum = (s) => { const [y, m, d] = s.split("-").map(Number); return Date.UTC(y, m - 1, d) / 86400000; };
export const isoDate = isDate;
const PH_COLOR = { hecho: "bg-emerald-500", en_curso: "bg-sky-500", pendiente: "bg-slate-300", bloqueado: "bg-red-500", no_aplica: "bg-slate-200" };

/** Recorrido de fases de una canción: un punto por fase, en orden. Se toca para cambiar (si se puede). */
export function PhasePipeline({ song, effective = {}, onCycle, compact = false }) {
  return (
    <div className={`flex items-center ${compact ? "gap-0.5" : "gap-1"}`}>
      {PHASES.map(([k, label], i) => {
        const e = effective[k] || {};
        const st = e.status || song.phases?.[k]?.status || "pendiente";
        const locked = !!e.auto || !onCycle;
        return (
          <div key={k} className="flex items-center">
            {i > 0 && <span className={`h-0.5 ${compact ? "w-1.5" : "w-3"} ${st === "hecho" ? "bg-emerald-400" : "bg-slate-200"}`} />}
            <button
              type="button" disabled={locked} onClick={() => onCycle && onCycle(k)}
              title={`${label}: ${STATUS[st]?.label || st}${e.auto ? ` · se marca solo (${e.note || ""})` : onCycle ? " · toca para cambiar" : ""}`}
              className={`flex items-center justify-center rounded-full font-bold text-white transition ${compact ? "h-4 w-4 text-[8px]" : "h-7 w-7 text-[10px]"} ${PH_COLOR[st] || PH_COLOR.pendiente} ${st === "pendiente" || st === "no_aplica" ? "!text-slate-600" : ""} ${locked ? "cursor-default" : "hover:ring-2 hover:ring-black/30"} ${e.auto ? "ring-2 ring-black/15" : ""}`}
            >
              {compact ? "" : label.slice(0, 2)}
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function PipelineLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
      {[["hecho", "Hecho"], ["en_curso", "En curso"], ["pendiente", "Pendiente"], ["bloqueado", "Bloqueado"]].map(([k, l]) => (
        <span key={k} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-full ${PH_COLOR[k]}`} />{l}</span>
      ))}
      <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-slate-300 ring-2 ring-black/15" />Con aro: se marca solo</span>
      <span className="text-slate-400">Vo · Be · Me · Má · Po · Vi · Le · Di</span>
    </div>
  );
}

/** Línea de tiempo tipo Gantt: una fila por canción, de la primera entrega a la salida. */
export function Gantt({ doc, effective = {}, onSong }) {
  const songs = doc?.songs || [];
  const first = /^\d{4}-\d{2}$/.test(doc?.start_month || "") ? doc.start_month : todayIso().slice(0, 7);
  const n = Math.max(1, Math.min(Number(doc?.months) || 6, 24));
  const months = Array.from({ length: n }, (_, i) => addMonths(first, i));
  const start = dayNum(`${first}-01`);
  const endYm = addMonths(first, n);
  const end = dayNum(`${endYm}-01`);
  const span = end - start;
  const pos = (s) => Math.max(0, Math.min(100, ((dayNum(s) - start) / span) * 100));
  const inRange = (s) => isDate(s) && dayNum(s) >= start && dayNum(s) < end;
  const today = todayIso();
  const tPos = inRange(today) ? pos(today) : null;
  const marks = (s) => {
    const out = [];
    for (const [k, label] of PHASES) {
      const d = s.phases?.[k]?.date;
      if (inRange(d)) out.push({ d, kind: "phase", label, st: (effective[s.id] || {})[k]?.status || s.phases[k].status });
    }
    if (inRange(s.video?.due)) out.push({ d: s.video.due, kind: "video", label: "Entrega de video" });
    if (inRange(s.cover?.due)) out.push({ d: s.cover.due, kind: "cover", label: "Entrega de portada" });
    for (const m of s.marketing || []) if (inRange(m.date)) out.push({ d: m.date, kind: "mkt", label: PLATFORMS.filter(([p]) => m[p]).map(([p, l]) => `${l}: ${m[p]}`).join(" · ") || "Contenido" });
    return out;
  };
  const projectMarks = (doc?.milestones || []).filter((m) => inRange(m.date));
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="rm-gantt">
      <div className="min-w-[860px]">
        <div className="grid grid-cols-[180px_1fr] border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
          <div className="px-3 py-2">Canción</div>
          <div className="relative flex">{months.map((ym) => <div key={ym} className="flex-1 border-l border-slate-200 px-1.5 py-2 first:border-l-0">{monthTitle(ym).replace(/ \d+$/, "").slice(0, 3)} {ym.slice(2, 4)}</div>)}</div>
        </div>
        {songs.map((s) => {
          const ms = marks(s);
          const rel = inRange(s.release_date) ? s.release_date : null;
          const dates = ms.map((m) => m.d).concat(rel ? [rel] : []).sort();
          const barFrom = dates.length ? pos(dates[0]) : null;
          const barTo = rel ? pos(rel) : dates.length ? pos(dates[dates.length - 1]) : null;
          return (
            <div key={s.id} className="grid grid-cols-[180px_1fr] border-b border-slate-100 last:border-0">
              <button type="button" onClick={() => onSong && onSong(s.id)} className="flex items-center gap-2 truncate px-3 py-3 text-left text-sm font-semibold text-black hover:underline" title={s.title}><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: songColor(songs, s.id) }} /><span className="truncate">{s.title}</span></button>
              <div className="relative h-12">
                {months.map((ym, i) => i > 0 && <div key={ym} className="absolute top-0 h-full border-l border-slate-100" style={{ left: `${(i / n) * 100}%` }} />)}
                {tPos !== null && <div className="absolute top-0 z-10 h-full w-px bg-[#CA0808]" style={{ left: `${tPos}%` }} />}
                {barFrom !== null && barTo !== null && <div className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full bg-slate-200" style={{ left: `${barFrom}%`, width: `${Math.max(barTo - barFrom, 0.8)}%` }} />}
                {ms.map((m, i) => (
                  <span key={i} title={`${fmtDate(m.d)} · ${m.label}`} className={`absolute top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white ${m.kind === "mkt" ? "h-2.5 w-2.5 bg-pink-400" : m.kind === "phase" ? `h-3 w-3 ${PH_COLOR[m.st] || PH_COLOR.pendiente}` : "h-3 w-3 bg-violet-500"}`} style={{ left: `${pos(m.d)}%` }} />
                ))}
                {rel && (
                  <span title={`Sale el ${fmtDate(rel, true)}`} className="absolute top-1/2 z-30 flex -translate-x-1/2 -translate-y-1/2 items-center" style={{ left: `${pos(rel)}%` }}>
                    <span className="h-4 w-4 rotate-45 rounded-[3px] border-2 border-white bg-black shadow" />
                  </span>
                )}
                {rel && <span className="absolute bottom-0.5 z-30 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-black" style={{ left: `${pos(rel)}%` }}>{rel.slice(8, 10)}/{rel.slice(5, 7)}</span>}
              </div>
            </div>
          );
        })}
        {projectMarks.length > 0 && (
          <div className="grid grid-cols-[180px_1fr] bg-slate-50/60">
            <div className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Proyecto</div>
            <div className="relative h-10">
              {projectMarks.map((m) => <span key={m.id} title={`${fmtDate(m.date)} · ${m.text}`} className={`absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rotate-45 border-2 border-white ${m.done ? "bg-slate-300" : "bg-amber-400"}`} style={{ left: `${pos(m.date)}%` }} />)}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3 border-t border-slate-200 px-3 py-2 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rotate-45 rounded-[2px] bg-black" /> Salida</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Fase (color = estado)</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-violet-500" /> Video / portada</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-pink-400" /> Contenido</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rotate-45 bg-amber-400" /> Hito del proyecto</span>
        <span className="flex items-center gap-1"><span className="h-3 w-px bg-[#CA0808]" /> Hoy</span>
      </div>
    </div>
  );
}

/** "Lo que toca ahora": atrasado + próximos 30 días, agrupado por semana. */
export function Agenda({ events, overdue = [] }) {
  const today = todayIso();
  const soon = events.filter((e) => e.date >= today && daysUntil(e.date) <= 30 && !e.done);
  const groups = [["Esta semana", (d) => d <= 6], ["La próxima semana", (d) => d > 6 && d <= 13], ["Más adelante (hasta 30 días)", (d) => d > 13]];
  const line = (e, i, late) => (
    <div key={i} className="flex items-start gap-3 py-2 text-sm">
      <div className={`w-14 shrink-0 text-center ${late ? "text-red-600" : "text-slate-500"}`}>
        <div className="text-lg font-bold leading-none text-black">{e.date.slice(8, 10)}</div>
        <div className="text-[10px] uppercase">{monthTitle(e.date.slice(0, 7)).slice(0, 3)}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-black">{e.parts ? e.parts.map((p) => `${p.l}: ${p.t}`).join(" · ") : e.text}</div>
        <div className="text-xs text-slate-500">{EVENT[e.type]?.label}{e.song ? ` · ${e.song}` : ""}{late ? ` · atrasado ${-daysUntil(e.date)} d` : ""}</div>
      </div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="rm-agenda">
      {overdue.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50/50 p-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-red-700">Atrasado</div>
          <div className="divide-y divide-red-100">{overdue.slice(0, 8).map((e, i) => line(e, i, true))}</div>
        </div>
      )}
      {groups.map(([title, f]) => {
        const items = soon.filter((e) => f(daysUntil(e.date)));
        if (!items.length) return null;
        return (
          <div key={title} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#CA0808]">{title}</div>
            <div className="divide-y divide-slate-100">{items.map((e, i) => line(e, i, false))}</div>
          </div>
        );
      })}
      {!soon.length && !overdue.length && <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">Nada en los próximos 30 días.</p>}
    </div>
  );
}

/** Fases con fecha pasada que no están hechas. */
export function overdueOf(doc, effective = {}) {
  const today = todayIso();
  const out = [];
  for (const s of doc?.songs || []) {
    for (const [k, label] of PHASES) {
      const d = s.phases?.[k]?.date;
      const st = (effective[s.id] || {})[k]?.status || s.phases?.[k]?.status;
      if (isDate(d) && d < today && !["hecho", "no_aplica"].includes(st)) out.push({ date: d, type: "entrega", text: label, song: s.title });
    }
    if (isDate(s.release_date) && s.release_date < today && (effective[s.id] || {}).distribucion?.status !== "hecho" && s.phases?.distribucion?.status !== "hecho") {
      out.push({ date: s.release_date, type: "salida", text: `«${s.title}» debía salir y no está distribuida`, song: s.title });
    }
  }
  for (const m of doc?.milestones || []) if (!m.done && isDate(m.date) && m.date < today && daysUntil(m.date) > -60) out.push({ date: m.date, type: "hito", text: m.text });
  return out.sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** Invertido contra generado, por canción (en USD). */
export function MoneyBars({ rows }) {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.cost || 0, r.income || 0)));
  return (
    <div className="space-y-3" data-testid="rm-money">
      {rows.map((r) => {
        const net = (r.income || 0) - (r.cost || 0);
        return (
          <div key={r.id} className="grid gap-2 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-[200px_1fr_140px] md:items-center">
            <div className="min-w-0"><div className="truncate font-semibold text-black">{r.title}</div>{r.note && <div className="text-xs text-slate-500">{r.note}</div>}</div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs"><span className="w-20 text-slate-500">Invertido</span><div className="h-3 flex-1 rounded-full bg-slate-100"><div className="h-3 rounded-full bg-[#CA0808]" style={{ width: `${((r.cost || 0) / max) * 100}%` }} /></div><span className="w-24 text-right tabular-nums text-slate-700">{money(r.cost)}</span></div>
              <div className="flex items-center gap-2 text-xs"><span className="w-20 text-slate-500">Generado</span><div className="h-3 flex-1 rounded-full bg-slate-100"><div className="h-3 rounded-full bg-emerald-500" style={{ width: `${((r.income || 0) / max) * 100}%` }} /></div><span className="w-24 text-right tabular-nums text-slate-700">{money(r.income)}</span></div>
            </div>
            <div className={`text-right text-sm font-bold tabular-nums ${net >= 0 ? "text-emerald-700" : "text-[#AC3333]"}`}>{net >= 0 ? "Se pagó solo" : `Falta ${money(-net)}`}</div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- agenda completa (filtrable, por mes)

const SONG_COLORS = ["#CA0808", "#2563EB", "#059669", "#D97706", "#7C3AED", "#DB2777", "#0891B2", "#65A30D", "#475569", "#B45309"];
export const songColor = (songs, id) => { const i = (songs || []).findIndex((s) => s.id === id); return i < 0 ? "#94A3B8" : SONG_COLORS[i % SONG_COLORS.length]; };
const WEEKDAYS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const weekday = (s) => { const [y, m, d] = s.split("-").map(Number); return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]; };
const TYPE_ORDER = [["salida", "Salidas"], ["entrega", "Entregas"], ["contenido", "Contenido"], ["pago", "Pagos"], ["hito", "Hitos"]];
const SHORT = { instagram: "IG", tiktok: "TT", youtube: "YT", offline: "Prensa" };

export function AgendaList({ events, songs = [], readOnly, onToggleMilestone, onRemoveMilestone, openAll = false }) {
  const today = todayIso();
  const thisMonth = today.slice(0, 7);
  const [types, setTypes] = useState(() => new Set(TYPE_ORDER.map(([k]) => k)));
  const [song, setSong] = useState("");
  const [past, setPast] = useState(openAll);
  const [open, setOpen] = useState({});
  if (!events.length) return <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">Todavía no hay nada fechado. Pon la fecha de salida de cada canción o añade un hito.</p>;

  const counts = Object.fromEntries(TYPE_ORDER.map(([k]) => [k, events.filter((e) => e.type === k).length]));
  const pastCount = events.filter((e) => e.date < today).length;
  const list = events.filter((e) => types.has(e.type) && (!song || e.song_id === song) && (past || e.date >= today));
  const months = [];
  for (const e of list) {
    const ym = e.date.slice(0, 7);
    if (!months.length || months[months.length - 1].ym !== ym) months.push({ ym, items: [] });
    months[months.length - 1].items.push(e);
  }
  const isOpen = (ym) => (openAll ? true : open[ym] !== undefined ? open[ym] : ym >= thisMonth && ym <= addMonths(thisMonth, 2));
  const toggleType = (k) => setTypes((t) => { const n = new Set(t); if (n.has(k)) n.delete(k); else n.add(k); return n.size ? n : new Set([k]); });

  return (
    <div className="space-y-3" data-testid="rm-agenda-list">
      {!openAll && (
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          {TYPE_ORDER.map(([k, l]) => counts[k] > 0 && (
            <button key={k} type="button" onClick={() => toggleType(k)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${types.has(k) ? EVENT[k].cls : "border-slate-200 bg-white text-slate-400 line-through"}`}>
              {l} <span className="opacity-60">{counts[k]}</span>
            </button>
          ))}
          {songs.length > 1 && (
            <select value={song} onChange={(e) => setSong(e.target.value)} className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
              <option value="">Todas las canciones</option>
              {songs.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          )}
          {pastCount > 0 && (
            <button type="button" onClick={() => setPast(!past)} className="ml-auto rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-black">
              {past ? "Ocultar lo que ya pasó" : `Ver lo que ya pasó (${pastCount})`}
            </button>
          )}
        </div>
      )}
      {!months.length && <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">Nada con esos filtros.</p>}
      {months.map(({ ym, items }) => {
        const o = isOpen(ym);
        const sum = TYPE_ORDER.map(([k, l]) => [k, l, items.filter((e) => e.type === k).length]).filter(([, , c]) => c);
        let todayShown = ym !== thisMonth;
        return (
          <div key={ym} className="overflow-hidden rounded-xl border border-slate-200 bg-white break-inside-avoid-page">
            <button type="button" onClick={() => !openAll && setOpen({ ...open, [ym]: !o })} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-slate-50">
              <span className={`text-sm font-bold capitalize ${ym === thisMonth ? "text-[#CA0808]" : "text-black"}`}>{monthTitle(ym)}{ym === thisMonth && <span className="ml-2 rounded-full bg-[#CA0808] px-2 py-0.5 text-[10px] font-bold uppercase text-white">este mes</span>}</span>
              <span className="flex flex-wrap gap-1.5">
                {sum.map(([k, l, c]) => <span key={k} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${EVENT[k].cls}`}>{c} {c === 1 && k !== "contenido" ? l.toLowerCase().replace(/s$/, "") : l.toLowerCase()}</span>)}
              </span>
              {!openAll && <span className="ml-auto text-xs text-slate-400">{o ? "▲" : "▼"}</span>}
            </button>
            {o && (
              <div className="divide-y divide-slate-100 border-t border-slate-100">
                {items.map((e, i) => {
                  const marker = !todayShown && e.date >= today;
                  if (marker) todayShown = true;
                  const isPast = e.date < today;
                  const color = songColor(songs, e.song_id);
                  return (
                    <div key={`${e.date}-${i}`}>
                      {marker && <div className="flex items-center gap-2 bg-red-50/60 px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-[#CA0808]"><span className="h-px flex-1 bg-[#CA0808]/40" />Hoy · {fmtDate(today)}<span className="h-px flex-1 bg-[#CA0808]/40" /></div>}
                      <div className={`flex items-start gap-3 px-4 py-2.5 ${isPast ? "opacity-55" : ""}`}>
                        <div className="w-11 shrink-0 text-center">
                          <div className="text-lg font-bold leading-none text-black">{Number(e.date.slice(8, 10))}</div>
                          <div className="text-[10px] uppercase text-slate-400">{weekday(e.date)}</div>
                        </div>
                        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: e.type === "salida" ? "#059669" : e.type === "entrega" ? "#0EA5E9" : e.type === "contenido" ? "#EC4899" : e.type === "pago" ? "#F59E0B" : "#94A3B8" }} title={EVENT[e.type].label} />
                        <div className="min-w-0 flex-1">
                          {e.parts ? (
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-black">
                              {e.parts.map((p) => <span key={p.k}><span className="mr-1 rounded bg-pink-50 px-1 text-[10px] font-bold text-pink-700">{SHORT[p.k] || p.l}</span>{p.t}</span>)}
                            </div>
                          ) : (
                            <div className={`text-sm ${e.type === "salida" ? "font-bold" : ""} ${e.done ? "text-slate-400 line-through" : "text-black"}`}>{e.text}</div>
                          )}
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                            <span>{e.mtype && e.mtype !== "hito" && e.mtype !== "pago" ? MILESTONE_TYPES[e.mtype] : EVENT[e.type].label}</span>
                            {e.song && <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-px font-semibold" style={{ color, background: `${color}14` }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />{e.song}</span>}
                          </div>
                        </div>
                        {e.milestone_id && !readOnly && (
                          <span className="flex shrink-0 gap-1 print:hidden">
                            <button type="button" onClick={() => onToggleMilestone(e.milestone_id)} className="rounded border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50">{e.done ? "Reabrir" : "Hecho"}</button>
                            <button type="button" onClick={() => onRemoveMilestone(e.milestone_id)} className="rounded border border-red-200 px-2 py-0.5 text-[11px] text-red-600 hover:bg-red-50">Quitar</button>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
